import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import axios from "axios";
import { showError } from '../toast/toastUtil';
import config from '../../config';
import { tokenExpiryManager } from '../tokenExpiryManager';
import { hasTokens } from '../auth/tokenValidator';
import {
  handle401Error,
  handleNetworkError,
  handleTokenValidationError,
  showSessionErrorModal,
  redirectToSignin
} from '../auth/sessionErrorHandler';

// 전역 세션 만료 모달 핸들러
let showSessionExpiredModal: ((message?: string) => void) | null = null;

export const setSessionExpiredModalHandler = (handler: (message?: string) => void) => {
  showSessionExpiredModal = handler;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void; // 성공
  reject: (reason?: any) => void; // 실패
  }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * 인증 문제인지 확인 (에러 메시지 기반)
 */
const isAuthenticationError = (error: AxiosError<{
  code?: string;
  message?: string;
  detailMessage?: string;
}>): boolean => {
  const responseData = error.response?.data as { detailMessage?: string; message?: string } | undefined;
  const message = responseData?.detailMessage
    || responseData?.message
    || error.message
    || '';
  
  const lowerMessage = message.toLowerCase(); // 문자열 값을 반환(소문자)
  
  // 인증 관련 키워드 확인
  return lowerMessage.includes('expired jwt token') ||
        lowerMessage.includes('expired') ||
        lowerMessage.includes('token') ||
        lowerMessage.includes('authentication') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('full authentication is required') ||
        lowerMessage.includes('invalid token') ||
        lowerMessage.includes('token expired');
};

/**
 * 인증이 필요한 요청인지 확인 (요청 컨텍스트 기반)
 */
const isAuthRequiredRequest = (originalRequest: InternalAxiosRequestConfig): boolean => {
  const url = originalRequest?.url || '';
  
  // 인증이 필요 없는 엔드포인트
  const noAuthEndpoints = [
    '/auth/signin',
    '/auth/refresh',
    '/auth/signup',
    '/auth/find',
    'business.juso.go.kr'
  ];
  
  // 인증이 필요 없는 엔드포인트는 제외
  if (noAuthEndpoints.some(endpoint => url.includes(endpoint))) {
    return false;
  }
  
  // Authorization 헤더가 있었는지 확인
  const hadAuthHeader = !!originalRequest?.headers?.Authorization;
  
  // 토큰이 존재했는지 확인
  const hadTokens = hasTokens();
  
  // 인증이 필요한 요청이고, 헤더를 보냈거나 토큰이 있었으면 인증 관련 요청
  return hadAuthHeader || hadTokens;
};

const showUnauthorizedMessage = (
  isDev: boolean,
  error: AxiosError<{
    code: string;
    message: string;
    detailMessage: string;
  }>
) => {
  if (isDev) {
    showError(`[DEV] 401 에러 발생 (인증 문제 아님): ${error.response?.data?.message || error.message}`);
  } else {
    showError('접근 권한이 없거나 요청을 처리할 수 없습니다.');
  }
};

//401 에러 처리 로직 함수
const handle401ErrorResponse = async (
  error: AxiosError<{
    code: string;
    message: string;
    detailMessage: string;
  }>,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  isDev: boolean
) => {
  const isAuthError = isAuthenticationError(error);
  const isAuthRequired = isAuthRequiredRequest(originalRequest);

  if (!isAuthError && !isAuthRequired) {
    showUnauthorizedMessage(isDev, error);
    return Promise.resolve(error.response);
  }

  if (originalRequest._retry || originalRequest.url?.includes('/auth/signin')) {
    handle401Error('api-call', error, false);
    return Promise.resolve(error.response);
  }

  if (originalRequest.url?.includes('/auth/refresh')) {
    handle401Error('token-refresh', error, false);
    return Promise.resolve(error.response);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(token => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return axios(originalRequest);
    }).catch(err => {
      return Promise.reject(err);
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  if (!hasTokens()) {
    handleTokenValidationError('api-call');
    return Promise.resolve(error.response);
  }

  const refreshToken = localStorage.getItem('refresh-token');

  try {
    const response = await axios.post(`${config.apiUrl}/auth/refresh`, { refreshToken });
    if (!response?.data?.successFlag) {
      throw new Error('토큰 갱신 실패');
    }

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;

    localStorage.setItem('access-token', accessToken);
    localStorage.setItem('refresh-token', newRefreshToken);

    processQueue(null, accessToken);

    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
    tokenExpiryManager.setTokens(accessToken, newRefreshToken);
    return axios(originalRequest);

  } catch (refreshError: any) {
    processQueue(refreshError, null);
    handle401Error('token-refresh', refreshError, false);
    return Promise.resolve(error.response);
  } finally {
    isRefreshing = false;
  }
};

// ============================================
// 1️⃣ 요청 인터셉터
// ============================================
export const onRequest = (config: InternalAxiosRequestConfig) => {
  // 인증이 필요 없는 엔드포인트
  const noAuthEndpoints = [
    '/auth/signin',
    '/auth/refresh',
    '/auth/signup',
    '/auth/find',
    'business.juso.go.kr'
  ];
  
  const needsAuth = !noAuthEndpoints.some(endpoint => 
    config.url?.includes(endpoint)
  );
  
  if (needsAuth) {
    const accessToken = localStorage.getItem('access-token');
    if (accessToken !== null) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  return config;
};

export const onErrorRequest = (error: Error) => {
  return Promise.reject(error);
};


// ============================================
// 2️⃣ 응답 인터셉터 (자동 갱신 처리)
// ============================================
export const onResponse = (response: AxiosResponse) => {
  // 백엔드가 자동 갱신한 토큰 확인 (대소문자 여러 버전 체크)
  const newAccessToken = response.headers['new-access-token'] || response.headers['New-Access-Token'] || response.headers['NEW-ACCESS-TOKEN'];
  if (newAccessToken) {
    localStorage.setItem('access-token', newAccessToken);
    // 갱신 시 만료 타이머 재설정
    const refreshToken = localStorage.getItem('refresh-token');
    tokenExpiryManager.setTokens(newAccessToken, refreshToken);
  }
  return response;
};

// ============================================
// 3️⃣ 에러 인터셉터 (수동 갱신 처리)
// ============================================
export const onErrorResponse = async (error: AxiosError<{
  code: string;
  message: string;
  detailMessage: string;
}>) => {
  try {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';

    // 401 에러 처리 (dev 환경 포함)
    if (error.response?.status === 401) {
      return handle401ErrorResponse(error, originalRequest, isDev);
    }

    //   dev 환경에서도 401이 아닌 에러의 responseData를 호출부에 전달
    // (400/500 등 HTTP 응답이 있는 경우 error.response를 resolve하여 호출부에서 response.data 사용 가능)
    if (isDev) {
      const errorType = !error.response
        ? 'NETWORK/CORS'
        : !error.response.status
        ? 'NO_STATUS'
        : error.response.status;

      const message = error.response?.data?.detailMessage
        || error.response?.data?.message
        || error.message
        || '알 수 없는 오류';

      showError(`[DEV] ${errorType} 에러: ${message}`);

      //   HTTP 응답이 있으면 그대로 반환해서 호출부에서 response.data 확인 가능
      if (error.response) {
        return Promise.resolve(error.response);
      }

      // 네트워크/CORS 등 응답이 없으면 기존처럼 종료(호출부는 catch로 처리 가능)
      return Promise.reject(error);
    }

    // ===== 이하 프로덕션 환경에서만 실행됨 =====

    // 401이 아닌 다른 에러 처리
    if (error.response) {
      //   응답 데이터가 없는 경우 처리 (백엔드 응답 구조 없음)
      if (!error.response?.data) {
        console.error('🔴 응답 데이터가 없는 에러:', error);
        showError('서버 응답 오류가 발생했습니다.');
        return Promise.reject(error);
      }

      const responseData = error.response.data;
      const message = responseData.detailMessage || responseData.message || '오류가 발생했습니다.';

      const isServerError =  (error.response.status ?? 0) >= 500;
      if (isServerError) {
        const specificPaths = ['/auth/refresh'];
        const isSpecificPath = specificPaths.some(path =>
          originalRequest.url?.includes(path)
        );

        if (isSpecificPath) {
          showSessionErrorModal('서버 오류가 발생했습니다. 다시 로그인해주세요.');
          return Promise.resolve(error.response);
        } else {
          showError(message, 5000);
        }
      } else {
        showError(message, 5000);
      }

      //   400/500 등 HTTP 응답이 있는 에러는 호출부에서 response.data를 쓰도록 response를 resolve
      return Promise.resolve(error.response);
    } else {
      // 네트워크 에러나 기타 에러
      handleNetworkError('network-error', error);
      return Promise.reject(error);
    }
  } catch (interceptorError: any) {
    // dev 환경에서는 인터셉터 에러도 표시
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';
    if (isDev) {
      showError('[DEV] 인터셉터 에러 발생');
      return Promise.reject(interceptorError);
    }
    
    handleNetworkError('interceptor-error', interceptorError);
    return Promise.reject(interceptorError);
  }
};

const axiosInterceptor = {
  onRequest,
  onErrorRequest,
  onResponse,
  onErrorResponse,
};

export default axiosInterceptor;
